import React, { useState, useEffect, useContext } from 'react'
import styled, { css } from 'styled-components'
import Slider from 'react-slick'
import { useStaticQuery, graphql } from 'gatsby'
import { rgba, stripUnit } from 'polished'

import { availableForSale } from 'utils/products'

import { breakpoints, media } from 'styles/media'
import { white } from 'styles/colors'
import * as spacing from 'styles/spacing'
import { navHeight, navHeightMobile, animationTime, animationCurve, square } from 'styles/global'

import Skin from './Skin'
import Header from 'components/Header'
import Icon from 'components/Icon'
import CheckoutContext from 'contexts/Checkout'
import NativeAuthContext from 'components/NativeAuth'

import LeftArrow from 'icons/left-arrow.svg'
import RightArrow from 'icons/right-arrow.svg'

const Container = styled.div`
  position: static;
`

const Inner = styled.div`
  position: relative;
  pointer-events: initial;
  z-index: 10;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: auto;
  background-color: #131111;
  padding-top: ${navHeightMobile};

  ${media.medium`
    padding-top: ${navHeight};
    height: 100vh;
  `};

  .slick-slider,
  .slick-list,
  .slick-track {
    height: 100%;
  }

  .slick-initialized .slick-slide {
    display: flex !important;
    justify-content: center;
    align-items: center;
  }

  .slick-track {
    display: flex;
    align-items: center;
  }

  .slick-slide {
    pointer-events: auto; /* Re-emable touch events */
    outline: 0;
    padding-top: 80px;
    padding-bottom: 72px;

    ${media.small`
      padding-top: 110px;
      padding-bottom: 80px;
    `};

    ${media.medium`
      padding-top: 0;
      padding-bottom: 0;
    `};

    > div {
      width: 100%;
    }
  }
`

const Arrow = styled.button`
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: ${white};
  position: absolute;
  top: -${navHeightMobile};
  bottom 0;
  z-index: 20;
  width: 80px;

  ${props =>
    props.prev &&
    css`
      left: 0;
      background: linear-gradient(-90deg, rgba(19, 17, 17, 0) 0%, #131111 100%);
    `}

  ${props =>
    props.next &&
    css`
      right: 0;
      background: linear-gradient(90deg, rgba(19, 17, 17, 0) 0%, #131111 100%);
    `}

  ${media.small`
    width: 120px;
  `};

  ${media.medium`
    width: 140px;
    top: -${navHeight};
  `};

  ${media.large`
    width: 160px;
  `};

  ${media.xLarge`
    width: 200px;
  `};
`

const ArrowCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.small};
  background-color: ${rgba(white, 0.15)};
  transition: background-color ${animationTime} ${animationCurve};
  border-radius: 50%;
  align-self: center;

  ${media.medium`
    padding: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};

  ${media.large`
    padding: ${spacing.medium};
  `};

  ${Arrow}:hover & {
    background-color: ${rgba(white, 0.25)};
  }
`

const ArrowIcon = styled.div`
  display: flex;

  > * {
    ${media.large`
      ${square('32px !important')};
    `};
  }
`

export default function SkinsSlider() {
  const { shopifyCollection } = useStaticQuery(graphql`
    query {
      shopifyCollection(handle: { eq: "epod-skins" }) {
        ...CartridgeCollectionFragment
      }
    }
  `)

  const [ready, setReady] = useState(false)

  useEffect(() => {
    setReady(true)
  })

  const { products } = useContext(CheckoutContext)
  const { province } = useContext(NativeAuthContext)

  const skins = shopifyCollection.products

  const settings = {
    infinite: true,
    arrows: true,
    slidesToShow: 9,
    focusOnSelect: true,
    centerMode: true,
    centerPadding: 0,
    slidesToScroll: 1,
    prevArrow: (
      <Arrow prev>
        <ArrowCircle>
          <ArrowIcon>
            <Icon icon={<LeftArrow />} />
          </ArrowIcon>
        </ArrowCircle>
      </Arrow>
    ),
    nextArrow: (
      <Arrow next>
        <ArrowCircle>
          <ArrowIcon>
            <Icon icon={<RightArrow />} />
          </ArrowIcon>
        </ArrowCircle>
      </Arrow>
    ),
    responsive: [
      {
        breakpoint: breakpoints.xLarge,
        settings: {
          slidesToShow: 7,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: breakpoints.large,
        settings: {
          slidesToShow: 7,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: breakpoints.medium,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 1,
          draggable: false,
          swipe: false
        }
      }
    ]
  }

  return ready ? (
    <Container>
      <Header transparent />
      <Inner>
        <Slider {...settings}>
          {/* Need to loop twice because number of skins is equal to slidesToShow and doesn't scroll */}
          {[...skins, ...skins].map((skin, idx) => {
            const fetchedProduct = products.find(p => skin.id.includes(p.shopifyGid))
            if (fetchedProduct && !fetchedProduct?.availableProvinces.includes(province)) return null
            const available = availableForSale({ variant: fetchedProduct?.variants[0], province })

            return <Skin skin={skin} key={idx} available={available} />
          })}
        </Slider>
      </Inner>
    </Container>
  ) : null
}
